import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Divider,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Fab,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import * as Icons from '../constants/icons';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_FAB_CTA_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { db } from '../firebase/firebase';
import { collection, addDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import type { SavedPlFilter } from '../hooks/useProfitAndLoss';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 保存済み条件を格納する Firestore コレクション
const SAVED_FILTERS_COLLECTION = 'HozenallCloud_Pl_SavedFilters';

// 予算バージョンの型 (損益計算書ページと共通)
type BudgetVersion = {
  version_code: string;
  version_name: string;
  sort_order: number;
};

// 会計年度セレクトの選択肢
type FiscalYearOption = { value: number; label: string };

// 拠点と部署の結合ロジック (and=かつ / or=または)。拠点・部署の両方が選択されたときのみ意味を持つ
export type BranchDeptLogic = 'and' | 'or';

// 適用される検索条件
export type ProfitAndLossFilterValues = {
  fiscalYear: number;
  budgetVersionCode: string;
  branchCodes: string[];
  departmentCodes: string[];
  // 省略時 (既存の保存済み条件) は 'and' として扱う
  branchDeptLogic?: BranchDeptLogic;
};

/**
 * 保存済み条件 1 行（D&D 並び替え対応）。
 * 左の ⠿ ハンドルのみドラッグ可。名前クリックで適用、ゴミ箱で削除。
 */
const SortableSavedRow: React.FC<{
  filter: SavedPlFilter;
  selected: boolean;
  deleting: boolean;
  onSelect: () => void;
  onDelete: () => void;
}> = ({ filter, selected, deleting, onSelect, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: filter.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        bgcolor: isDragging ? '#f1f5f9' : selected ? '#eff6ff' : 'background.paper',
        boxShadow: isDragging ? 3 : 0,
        '&:hover': { bgcolor: selected ? '#e0ecff' : '#f8fafc' },
      }}
    >
      {/* ⠿ ドラッグハンドル（ここだけが掴める） */}
      <Tooltip title="ドラッグで並び替え">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.disabled',
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <Icons.DragIndicator sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>

      {/* 名前（クリックで適用） */}
      <Box
        onClick={onSelect}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          fontSize: '13px',
          py: 0.5,
          fontWeight: selected ? 700 : 400,
          color: selected ? '#1e3a8a' : 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {filter.name}
      </Box>

      {/* 削除 */}
      <Tooltip title="この条件を削除">
        <span>
          <IconButton size="small" edge="end" disabled={deleting} onClick={onDelete}>
            {deleting ? (
              <CircularProgress size={16} />
            ) : (
              <Icons.Delete sx={{ fontSize: 18, color: 'text.disabled' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

interface ProfitAndLossSearchFiltersProps {
  fiscalYear: number;
  budgetVersionCode: string;
  branchCodes: string[];
  departmentCodes: string[];
  branchDeptLogic: BranchDeptLogic;
  fiscalYearOptions: FiscalYearOption[];
  budgetVersions: BudgetVersion[];
  branchList: any[];
  departmentList: any[];
  // 保存済み条件一覧（フックでリアルタイム購読したものを受け取る）
  savedFilters: SavedPlFilter[];
  // 適用中の保存条件 id（保存済み条件セレクトの選択表示用）。手動適用・デフォルト時は null
  appliedFilterId?: string | null;
  // appliedName/appliedId に保存条件を渡すと「適用中」表示・選択表示に使われる。手動適用時はいずれも null
  onApply: (values: ProfitAndLossFilterValues, appliedName?: string | null, appliedId?: string | null) => void;
  // 適用中の保存条件が削除されたときに親へ通知し、適用表示をクリアさせる
  onAppliedRemoved?: () => void;
}

/**
 * 損益計算書ページの検索フィルター (Popover 内に展開)。
 * 会計年度・予算パターン・拠点 (複数)・部署 (複数) を draft として編集し、
 * ［実行］押下時にまとめて親へ反映する。即時フェッチは行わない。
 */
const ProfitAndLossSearchFilters: React.FC<ProfitAndLossSearchFiltersProps> = ({
  fiscalYear,
  budgetVersionCode,
  branchCodes,
  departmentCodes,
  branchDeptLogic,
  fiscalYearOptions,
  budgetVersions,
  branchList,
  departmentList,
  savedFilters,
  appliedFilterId,
  onApply,
  onAppliedRemoved,
}) => {
  // --------------------------------------------------
  // 📥 draft (ローカル) state — 親の適用値が変わったら同期する
  // --------------------------------------------------
  const [localFiscalYear, setLocalFiscalYear] = useState<number>(fiscalYear);
  const [localBudgetVersion, setLocalBudgetVersion] = useState<string>(budgetVersionCode);
  const [localBranchCodes, setLocalBranchCodes] = useState<string[]>(branchCodes);
  const [localDepartmentCodes, setLocalDepartmentCodes] = useState<string[]>(departmentCodes);
  const [localBranchDeptLogic, setLocalBranchDeptLogic] = useState<BranchDeptLogic>(branchDeptLogic);

  useEffect(() => { setLocalFiscalYear(fiscalYear); }, [fiscalYear]);
  useEffect(() => { setLocalBudgetVersion(budgetVersionCode); }, [budgetVersionCode]);
  useEffect(() => { setLocalBranchCodes(branchCodes); }, [branchCodes]);
  useEffect(() => { setLocalDepartmentCodes(departmentCodes); }, [departmentCodes]);
  useEffect(() => { setLocalBranchDeptLogic(branchDeptLogic); }, [branchDeptLogic]);

  // --------------------------------------------------
  // 📌 保存済み条件（一覧はフックでリアルタイム購読。ここでは保存・削除のみ）
  // --------------------------------------------------
  const { user } = useAuth();
  const notify = useNotification();
  const confirm = useConfirm();

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 保存済み条件ドロップダウン（自前。MUI Select の内部メニュー制約を避けて D&D を成立させる）
  const [savedAnchorEl, setSavedAnchorEl] = useState<HTMLElement | null>(null);
  // D&D の楽観的並び順（onSnapshot で確定値が来たら同期）
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  useEffect(() => {
    setOrderedIds(savedFilters.map((f) => f.id));
  }, [savedFilters]);
  const orderedFilters = orderedIds
    .map((id) => savedFilters.find((f) => f.id === id))
    .filter((f): f is SavedPlFilter => Boolean(f));

  const sensors = useSensors(
    // 4px 動かすまではドラッグ開始しない（クリックとの誤判定を防ぐ）
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ドロップ確定 → 並んだ順に sort_order を採番して保存（onSnapshot で全クライアントに反映）
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newIds = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(newIds); // 楽観的更新
    try {
      const batch = writeBatch(db);
      newIds.forEach((id, idx) => {
        batch.update(doc(db, SAVED_FILTERS_COLLECTION, id), { sort_order: idx });
      });
      await batch.commit();
    } catch (e) {
      console.error('並び替えの保存に失敗:', e);
      notify.error('並び替えの保存に失敗しました。');
      setOrderedIds(savedFilters.map((f) => f.id)); // 失敗時は元に戻す
    }
  };

  // 保存済み条件を選択 → その条件をそのまま親へ反映（即適用）
  const handleSelectSaved = (id: string) => {
    const target = savedFilters.find((f) => f.id === id);
    if (!target) return;
    onApply({
      fiscalYear: target.filters.fiscalYear,
      budgetVersionCode: target.filters.budgetVersionCode,
      branchCodes: target.filters.branchCodes ?? [],
      departmentCodes: target.filters.departmentCodes ?? [],
      // フラグ導入前に保存された条件は 'and' (従来動作) として扱う
      branchDeptLogic: target.filters.branchDeptLogic ?? 'and',
    }, target.name, target.id);
  };

  // 保存済み条件の削除（行ごとのゴミ箱から呼ぶ）
  const handleDeleteSaved = async (target: SavedPlFilter) => {
    const ok = await confirm({
      title: '保存済み条件の削除',
      message: `「${target.name}」を削除します。よろしいですか？`,
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(target.id);
    try {
      await deleteDoc(doc(db, SAVED_FILTERS_COLLECTION, target.id));
      // 一覧はフックの onSnapshot が自動反映する
      // 適用中の条件を削除した場合は「適用中」表示も解除する
      if (appliedFilterId === target.id) onAppliedRemoved?.();
      notify.success('保存済み条件を削除しました。');
    } catch (e) {
      console.error('保存済み条件の削除に失敗:', e);
      notify.error('削除に失敗しました。');
    } finally {
      setDeletingId(null);
    }
  };

  // 条件を保存ダイアログ
  const handleOpenSaveDialog = () => {
    setFilterName('');
    setIsSaveDialogOpen(true);
  };
  const handleCloseSaveDialog = () => {
    setIsSaveDialogOpen(false);
    setFilterName('');
  };
  // 保存実行 — 現在の draft（会計年度・予算パターン・拠点・部署）を保存
  const handleSaveFilter = async () => {
    if (!user?.email || !filterName.trim()) return;
    setIsSaving(true);
    try {
      await addDoc(collection(db, SAVED_FILTERS_COLLECTION), {
        user_id: user.email,
        name: filterName.trim(),
        filters: {
          fiscalYear: localFiscalYear,
          budgetVersionCode: localBudgetVersion,
          branchCodes: localBranchCodes,
          departmentCodes: localDepartmentCodes,
          branchDeptLogic: localBranchDeptLogic,
        },
        created_at: serverTimestamp(),
      });
      // 一覧はフックの onSnapshot が自動反映する
      notify.success('検索条件を保存しました。');
      handleCloseSaveDialog();
    } catch (e) {
      console.error('検索条件の保存に失敗:', e);
      notify.error('保存に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  // 検索実行 — draft をまとめて親へ反映（手動適用なので「適用中」表示は解除）
  const handleSearch = () => {
    onApply({
      fiscalYear: localFiscalYear,
      budgetVersionCode: localBudgetVersion,
      branchCodes: localBranchCodes,
      departmentCodes: localDepartmentCodes,
      branchDeptLogic: localBranchDeptLogic,
    }, null, null);
  };

  // クリア — 今月が属する会計年度 (9月開始・8月決算)・先頭予算バージョン・拠点空・部署空へ戻す
  // ※ このボタンでは検索実行されない (draft をクリアするのみ。反映には［検索を実行］が必要)
  const handleClear = () => {
    const now = new Date();
    const currentFy = now.getMonth() + 1 >= 9 ? now.getFullYear() + 1 : now.getFullYear();
    const firstVersion = budgetVersions.length > 0 ? budgetVersions[0].version_code : '';
    setLocalFiscalYear(currentFy);
    setLocalBudgetVersion(firstVersion);
    setLocalBranchCodes([]);
    setLocalDepartmentCodes([]);
    setLocalBranchDeptLogic('and');
  };

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        {/* 📌 保存済み条件（選択で即適用）— 保存が 1 件以上あるときだけ表示 */}
        {savedFilters.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
              >
                保存済み条件
              </Typography>
              {/* Select 風トリガー（クリックで自前ドロップダウンを開く） */}
              <Box
                onClick={(e) => setSavedAnchorEl(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  border: '1px solid',
                  borderColor: Boolean(savedAnchorEl) ? 'primary.main' : 'rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  px: 1.5,
                  py: '8.5px',
                  cursor: 'pointer',
                  backgroundColor: 'background.paper',
                  fontSize: '13px',
                  '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
                }}
              >
                {(() => {
                  const current = appliedFilterId
                    ? savedFilters.find((f) => f.id === appliedFilterId)
                    : undefined;
                  return current ? (
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {current.name}
                    </Box>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>保存済み条件を選択して適用</span>
                  );
                })()}
                <Icons.ArrowDropDown sx={{ color: 'action.active', flexShrink: 0 }} />
              </Box>

              <Popover
                open={Boolean(savedAnchorEl)}
                anchorEl={savedAnchorEl}
                onClose={() => setSavedAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    width: savedAnchorEl?.clientWidth,
                    maxHeight: 320,
                    p: 0.5,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: '1px solid #edf2f7',
                  },
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', px: 1, pt: 0.5, pb: 1, color: 'text.secondary' }}>
                  クリックで適用・⠿ を掴んで並び替え
                </Typography>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                    {orderedFilters.map((f) => (
                      <SortableSavedRow
                        key={f.id}
                        filter={f}
                        selected={appliedFilterId === f.id}
                        deleting={deletingId === f.id}
                        onSelect={() => {
                          setSavedAnchorEl(null);
                          handleSelectSaved(f.id);
                        }}
                        onDelete={() => handleDeleteSaved(f)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Popover>
            </Box>

            <Divider sx={{ mb: 2 }} />
          </>
        )}

        <Grid container spacing={2}>
          {/* 会計年度 */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>会計年度</InputLabel>
              <Select
                label="会計年度"
                value={localFiscalYear}
                onChange={(e) => setLocalFiscalYear(Number(e.target.value))}
                sx={{ fontSize: '13px', backgroundColor: 'background.paper' }}
              >
                {fiscalYearOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '13px' }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 予算パターン */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>予算パターン</InputLabel>
              <Select
                label="予算パターン"
                value={localBudgetVersion}
                displayEmpty
                onChange={(e) => setLocalBudgetVersion(e.target.value)}
                sx={{ fontSize: '13px', backgroundColor: 'background.paper' }}
              >
                {budgetVersions.length === 0 && (
                  <MenuItem value="" disabled sx={{ fontSize: '13px' }}>予算バージョンなし</MenuItem>
                )}
                {budgetVersions.map((v) => (
                  <MenuItem key={v.version_code} value={v.version_code} sx={{ fontSize: '13px' }}>
                    {v.version_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 拠点 (複数選択) */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              size="small"
              options={branchList}
              getOptionLabel={(opt: any) => opt.branch_name || ''}
              isOptionEqualToValue={(opt: any, val: any) => opt.branch_code === val.branch_code}
              value={branchList.filter((b: any) => localBranchCodes.includes(b.branch_code))}
              onChange={(_e, newValue) => {
                setLocalBranchCodes(newValue.map((b: any) => b.branch_code));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="拠点"
                  placeholder={localBranchCodes.length === 0 ? '拠点で絞り込み (複数選択可能)' : ''}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                    '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option: any, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.branch_name}
                      {...tagProps}
                      size="small"
                      sx={{ fontSize: '11px', height: '20px' }}
                    />
                  );
                })
              }
            />
          </Grid>

          {/* 拠点と部署の結合ロジック (両方選択時のみ有効。片方のみは無効化し AND と同義) */}
          <Grid size={{ xs: 12 }}>
            {(() => {
              const logicEnabled = localBranchCodes.length > 0 && localDepartmentCodes.length > 0;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 0.5 }}>
                  {/* 説明文に押されてボタンのラベルが折り返さないよう、グループは縮小禁止・ラベルは改行禁止にする */}
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={localBranchDeptLogic}
                    disabled={!logicEnabled}
                    onChange={(_e, v: BranchDeptLogic | null) => { if (v) setLocalBranchDeptLogic(v); }}
                    sx={{ flexShrink: 0 }}
                  >
                    <ToggleButton value="and" sx={{ fontSize: '11px', py: 0.25, px: 1.25, textTransform: 'none', whiteSpace: 'nowrap' }}>
                      かつ (AND)
                    </ToggleButton>
                    <ToggleButton value="or" sx={{ fontSize: '11px', py: 0.25, px: 1.25, textTransform: 'none', whiteSpace: 'nowrap' }}>
                      または (OR)
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                    {logicEnabled
                      ? (localBranchDeptLogic === 'or'
                        ? '選択拠点の全部署 ＋ 全拠点の選択部署 を集計します'
                        : '選択拠点、かつ、選択部署 に該当する部門のみ集計します')
                      : '拠点と部署を両方選択したときに切替できます'}
                  </Typography>
                </Box>
              );
            })()}
          </Grid>

          {/* 部署 (複数選択) */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              size="small"
              options={departmentList}
              getOptionLabel={(opt: any) => opt.department_name || ''}
              isOptionEqualToValue={(opt: any, val: any) => opt.department_code === val.department_code}
              value={departmentList.filter((d: any) => localDepartmentCodes.includes(d.department_code))}
              onChange={(_e, newValue) => {
                setLocalDepartmentCodes(newValue.map((d: any) => d.department_code));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="部署"
                  placeholder={localDepartmentCodes.length === 0 ? '部署で絞り込み (複数選択可能)' : ''}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                    '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option: any, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.department_name}
                      {...tagProps}
                      size="small"
                      sx={{ fontSize: '11px', height: '20px' }}
                    />
                  );
                })
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* アクションボタン */}
      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{
        p: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#f1f5f9',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handleClear}
            startIcon={<Icons.RestartAlt />}
            sx={STANDARD_TEXT_BUTTON_SX}
          >
            クリア
          </Button>

          <Fab
            variant="extended"
            size="small"
            onClick={handleOpenSaveDialog}
            sx={STANDARD_FAB_CTA_SX}
          >
            <Icons.Save />
            条件を保存
          </Fab>
        </Box>

        <Fab
          variant="extended"
          size="small"
          disableRipple
          onClick={handleSearch}
          sx={STANDARD_FAB_CTA_SX}
        >
          <Icons.Search />
          検索を実行
        </Fab>
      </Box>

      {/* 条件を保存ダイアログ */}
      <Dialog open={isSaveDialogOpen} onClose={handleCloseSaveDialog}>
        <DialogTitle
          sx={{ fontSize: '0.875rem', fontWeight: 'bold', py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Icons.Save sx={{ fontSize: 18 }} />
          検索条件を保存
        </DialogTitle>
        <DialogContent sx={{ minWidth: '320px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            現在の絞り込み内容（会計年度・予算パターン・拠点・部署・かつ／または）を保存します。
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="条件名"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="例：本社・期初予算"
            sx={{
              '& .MuiInputLabel-root': { fontSize: '13px' },
              '& .MuiOutlinedInput-input': { fontSize: '13px' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseSaveDialog} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button
            onClick={handleSaveFilter}
            disabled={isSaving || !filterName.trim()}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            {isSaving ? '保存中...' : '保存する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfitAndLossSearchFilters;
